import React from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import Select, {
  IProps as ISelectProps,
  IValue,
  IValues,
} from './_module/Select';
import {
  IUserQueryData,
  IUserVars,
  IUserVarsPartial,
  userQuery,
} from '../../../graphql/query/user/useUserQuery';
import SchemaUserAvatar from '../_schema/SchemaUserAvatar';

interface IProps<TValue extends IValue | IValues> extends ISelectProps<TValue> {
  queryVariableSort?: IUserVars['sort'];
  useUserAvatar?: boolean;
  searchByField?: keyof IUserVars['search'];
}

export default function SelectSearchUser<TValue extends IValue | IValues>(
  props: IProps<TValue>
) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const {
    creatable,
    disabled,
    label,
    onChange,
    placeholder,
    value,
    menuPosition,
    required,
    margin,
    error,
    fullWidth,
    helperText,
    isMulti,
    queryVariableSort,
    useUserAvatar,
    searchByField,
  } = props;

  return (
    <Select<TValue>
      creatable={creatable}
      isMulti={isMulti}
      async
      isClearable
      loadOptions={async (input) => {
        const { data } = await client.query<IUserQueryData, IUserVarsPartial>({
          query: userQuery(`
                _id
                profile {
                  name {
                    firstname
                    lastname
                  }
                  ${
                    useUserAvatar
                      ? `
                          media {
                            images {
                              avatar {
                                status
                                filename
                                filename_small
                                filename_medium
                                
                                filenameUrl
                                filenameSmallUrl
                                filenameMediumUrl
                              }
                            }
                          }
                          `
                      : ''
                  }
                }
              `),
          variables: {
            search: {
              [searchByField || '_id']: input,
            },
            sort: queryVariableSort || {},
            limit: 5,
          },
        });

        if (data && data.users) {
          return data.users.items.map((user) => {
            let name = '';
            const { profile } = user;
            if (profile && profile.name && profile.name.firstname)
              name += `${profile.name.firstname} `;
            if (profile && profile.name && profile.name.lastname)
              name += profile.name.lastname;
            return {
              label: name,
              value: user._id,
              startComponent: useUserAvatar ? (
                <SchemaUserAvatar width={3} height={3} user={user} />
              ) : undefined,
            };
          });
        }

        return [];
      }}
      menuPosition={menuPosition}
      label={label}
      required={required}
      margin={margin}
      error={error}
      disabled={disabled}
      fullWidth={fullWidth}
      helperText={helperText}
      value={value}
      onChange={onChange}
      placeholder={placeholder || t('enter__type__', { type: t('user_name') })}
    />
  );
}
